/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { joiResolver } from '@hookform/resolvers/joi';
import { Checkbox, TextField, Button, Dropdown, Icon, RadioButton, Tooltip, Toggle, Textarea, copyIcon, DropdownProps } from '@marketplace/ui-elements';
import {
  useAPIFetchTechDetailsContext,
  useAPIFetchTechDetails,
  useAPIPostPutTechDetails,
  useAPIPublishApplication,
  TLaunchType,
  TLaunchTypeNames,
  TAuthTypes,
  TApplicationLaunchType,
  TTechDetailsResponse,
  TTechDetails,
  TProduction,
  TBody,
  ToAuthClientMechanism,
  TAppStatus,
  TFieldNames,
  useAPIResetSecretApplication,
  useAPISubmitToPublish
} from '@marketplace/api';
import AppDetailsWrapper from './components/app-details-wrapper';
import AppDetailsTitle from './components/app-details-title';
import { ScopesItem } from './components/scopes-item';
import { techDetailsFormValidationSchema } from './tech-details.validation';

import classes from './tech-details-form.module.scss';
import { alt, date } from 'joi';
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { MARKETPLACE_DEV_CONTEXT } from '../../../../../../../../libs/common/src/features/auth';
import { UserType } from '../../../../../../../../libs/common/src/features/auth/auth.types';


export const TechDetailsForm = ({ apiResponseErrors, onChangeIsApplicationInEditMode, isCreated }: any) => {
  const { formatMessage } = useIntl();
  const { id: appId } = useParams<{ id: string }>();
  const [allscope, setallscope] = useState(false);
  const [isSecretVisible, setIsisSecretVisible] = useState(false);
  const [isContentEditable, setIsContentEditable] = useState(false);
  const [isProductionDisabled, setIsProductionDisabled] = useState(false);
  const [isLauchTypeLocked, setIsLauchTypeLocked] = useState(false);
  const { resetSecret, loading: resetClientSeretLoading } = useAPIResetSecretApplication();
  const [initialData, setInitialData] = useState<TTechDetailsResponse['data'] | any>({
    application_launch_type: null,
    has_patient_context: false,
    has_encounter_context: false,
    token_introspection: null,
    oauthclienttype: null,
    oauthclientmechanism: null,
    client_secret: '',
    scopes: [],
    sandbox: {
      client_id: '',
      smart_launch_url_sandbox: '',
      redirect_uri_sandbox: '',
    },
    production: {
      client_id: '',
      smart_launch_url_production: '',
      redirect_uri_production: '',
    },
    jwks: '',
    scope_read_all: false,
    status: null,
    secret_expiry_date: null
  });

  useEffect(() => {
    onChangeIsApplicationInEditMode(onChangeIsApplicationInEditMode?.TECH_DETAILS_TAB, isContentEditable);
  }, [isContentEditable]);

  const [checkedScope, setCheckedScope] = useState<'individual' | 'population' | 'system'>('individual');
  const isEditOn = () => setIsContentEditable(true);
  const isEditOff = () => setIsContentEditable(false);

  const techDetailsContext = useAPIFetchTechDetailsContext();
  const techDetails = useAPIFetchTechDetails();
  const { postTechDetails, loading: postPutTechDetailsLoading } = useAPIPostPutTechDetails();
  useAPIPublishApplication();
  const { data: submitToPublishResponse } = useAPISubmitToPublish();

  const standaloneFields =
    techDetailsContext.data?.application_launch_types.filter((i) => i.type === TLaunchType['STANDALONE']) || [];

  const ehrFields =
    techDetailsContext.data?.application_launch_types.filter((i) => i.type === TLaunchType['EHR_EMBEDDED']) || [];

  const backEndFields = techDetailsContext.data?.application_launch_types.find(
    (i) => i.name === TLaunchTypeNames['BACKEND_SERVICE']

  );
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { isDirty, errors, isValid },
  } = useForm<TTechDetails>({
    mode: 'onChange',
    resolver: joiResolver(techDetailsFormValidationSchema({ formatMessage })),
  });

  const [errorList, setErrorList] = useState<any>([]);

  const setResponseErrors = (response: any) => {
    const errorsList = response?.errors?.reduce((acc: any, item: any) => {
      const errorfield = item.source;
      return { ...acc, [errorfield]: item.message };
    }, {});
    setErrorList(errorsList);
  };

  const compareSmartAndRedirectUrl = () => {
    if ((smartUrl === redirectUrl) && isContentEditable) {
      setError('production.redirect_uri_production', {
        message: formatMessage(
          { id: 'technicalDetailsForm_identical_url' },
          {
            field: "Redirect URI"
          }
        ),
      });
      return
    }
  }
  const isButtonDisabled = (scope: string) => {
    if (!isContentEditable) return true;
    switch (scope) {
      case 'population':
        return !isSystemScopesDisplayed && !isIndiviualScopesDisplayed;
      case 'individual':
        return !isPopulationScopesDisplayed && !isSystemScopesDisplayed;
      case 'system':
        return !isPopulationScopesDisplayed && !isIndiviualScopesDisplayed;
      default:
        return false;
    }
  };
  const getScopeReadAll = (field: any) => {
    return field ? field : false;
  };
  const getScopes = (field: any) => (field && Array.isArray(field)) ? field : [];
  const handleSaveDetails = async (data?: any) => {
    const hasError = validateForm(data)
    if (!hasError) {
      if (isValid && isDirty) {
        const scopes_selected = processScopes(data?.scopes, isSystemScopesDisplayed);

        const oauthclientmechanism = data?.oauthclienttype === 'CONFIDENTIAL' ? data?.oauthclientmechanism : null
        const dataForRequest: TBody = {
          applicationId: appId,
          body: {
            application_launch_type: data.application_launch_type?.id,
            has_patient_context: !!data.has_patient_context,
            has_encounter_context: !!data.has_encounter_context,
            token_introspection: data?.token_introspection,
            scopes: getScopes(scopes_selected),
            auth_type: data?.auth_type,
            production: data?.production,
            jwks: data?.jwks,
            client_secret: data?.secret,
            oauthclienttype: data?.oauthclienttype,
            oauthclientmechanism: oauthclientmechanism,
            tenants: data?.tenants ? data.tenants : [],
            scope_read_all: getScopeReadAll(data.scope_read_all),

          },
        };

        const response = await postTechDetails(dataForRequest);
        const convertedData = convertTechDetailData(response.data);
        if (response.errors) {
          setResponseErrors(response);
          setInitialData(convertedData)
          return
        }
        setInitialData(convertedData)
        reset(convertedData)
        setValue('client_secret', response.data.client_secret);
        setSecret(response?.data.client_secret);
        isEditOff();

      }
    }
  };

  const validateForm = (data: any) => {
    if (!isContentEditable) {
      return;
    }
    const validationRules = [
      {
        condition: isProductionDisabled && !data.production.smart_launch_url_production && !data.production.redirect_uri_production && data.application_launch_type.name !== TLaunchTypeNames['BACKEND_SERVICE'],
        fields: [
          {
            field: TFieldNames.ProductionSmartLaunchUrl,
            message: formatMessage(
              { id: 'generalForm_validation_required' },
              {
                field: "Smart Launch URL"
              }
            ),
          },
          {
            field: TFieldNames.ProductionRedirectUri,
            message: formatMessage(
              { id: 'generalForm_validation_required' },
              {
                field: "Redirect URI"
              }
            ),
          }
        ]
      },
      {
        condition: isProductionDisabled && !data.production.smart_launch_url_production && data.application_launch_type.name !== TLaunchTypeNames['BACKEND_SERVICE'],
        fields: [
          {
            field: TFieldNames.ProductionSmartLaunchUrl,
            message: formatMessage(
              { id: 'generalForm_validation_required' },
              {
                field: "Smart Launch URL"
              }
            ),
          }
        ]
      },
      {
        condition: isProductionDisabled && !data.production.redirect_uri_production && data.application_launch_type.name !== TLaunchTypeNames['BACKEND_SERVICE'],
        fields: [
          {
            field: TFieldNames.ProductionRedirectUri,
            message: formatMessage(
              { id: 'generalForm_validation_required' },
              {
                field: "Redirect URI"
              }
            ),
          }
        ]
      },
      {
        condition: data.production.smart_launch_url_production && data.production.redirect_uri_production && (data.production.smart_launch_url_production === data.production.redirect_uri_production) && data.application_launch_type.name !== TLaunchTypeNames['BACKEND_SERVICE'],
        fields: [
          {
            field: TFieldNames.ProductionRedirectUri,
            message: formatMessage(
              { id: 'technicalDetailsForm_identical_url' },
              {
                field: "Redirect URI"
              }
            ),
          }
        ]
      },
      {
        condition: !data.oauthclienttype,
        fields: [
          {
            field: TFieldNames.OAuthClientType,
            message: formatMessage(
              { id: 'generalForm_validation_required' },
              {
                field: formatMessage({
                  id: 'technicalDetailsForm_auth_type_title',
                }),
              }
            ),
          }
        ]
      },
      {
        condition: isConfidential && !data.oauthclientmechanism,
        fields: [
          {
            field: TFieldNames.OAuthClientMechanism,
            message: formatMessage(
              { id: 'generalForm_validation_required' },
              {
                field: formatMessage({
                  id: 'technicalDetailsForm_auth_mechanism',
                }),
              }
            ),
          }
        ]
      },
      {
        condition: isConfidential && isJwks && !jwksValue,
        fields: [
          {
            field: TFieldNames.Jwks,
            message: formatMessage(
              { id: 'generalForm_validation_required' },
              {
                field: formatMessage({
                  id: 'jwks',
                }),
              }
            ),
          }
        ]
      }
    ]
    for (const rule of validationRules) {
      if (rule.condition) {
        for (const { field, message } of rule.fields) {
          setError(field, { message });
        }
        return true;
      }
    }
    return false;
  }
  const processScopes = (scopes: Array<any>, isSystemScopesDisplayed: boolean): Array<any> => {
    const scopes_selected: Array<any> = [];
    scopes.forEach((item: any) => {
      if (!isSystemScopesDisplayed) {
        item.system_r = false;
        item.system_w = false;
      } else {
        item.individual_patient_r = false;
        item.individual_patient_w = false;
        item.patient_population_r = false;
        item.patient_population_w = false;
      }

      if (item.individual_patient_r || item.individual_patient_w || item.patient_population_r || item.patient_population_w || item.system_r || item.system_w) {
        scopes_selected.push(item);
      }
    });
    return scopes_selected;
  };

  useEffect(() => {
    const validationRules = [
      {
        field: TFieldNames.Scopes,
        conditions: [apiResponseErrors?.scopes, errorList?.scopes],
        message: formatMessage({ id: 'error_scope_one_must_be_chosen' }),
      },
      {
        field: TFieldNames.ApplicationLaunchType,
        conditions: [apiResponseErrors?.application_launch_type || errorList?.application_launch_type],
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'technicalDetailsForm_application_type_title',
            }),
          }
        ),
      },
      {
        field: TFieldNames.Jwks,
        conditions: [apiResponseErrors?.jwks || errorList?.jwks],
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'jwks',
            }),
          }
        ),
      },
      {
        field: TFieldNames.OAuthClientType,
        conditions: [apiResponseErrors?.oauthclienttype || errorList?.oauthclienttype],
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'technicalDetailsForm_auth_type_title',
            }),
          }
        ),
      },
      {
        field: TFieldNames.OAuthClientMechanism,
        conditions: [apiResponseErrors?.oauthclientmechanism || errorList?.oauthclientmechanism],
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'technicalDetailsForm_auth_mechanism',
            }),
          }
        ),
      },
      {
        field: TFieldNames.Token_Introspection,
        conditions: [apiResponseErrors?.token_introspection || errorList?.token_introspection],
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'technicalDetailsForm_token_introspection_title',
            }),
          }
        ),
      },
      {
        field: TFieldNames.Has_Patient_Context,
        conditions: [apiResponseErrors?.has_patient_context ||
          apiResponseErrors?.has_encounter_context ||
          errorList?.has_patient_context ||
          errorList?.has_encounter_context],
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'technicalDetailsForm_launch_context_title',
            }),
          }
        ),

      },
      {
        field: TFieldNames.ProductionRedirectUri,
        conditions: [apiResponseErrors?.redirect_uri_production ||
          submitToPublishResponse?.redirect_uri_production ||
          errorList['production/redirect_uri_production']],
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'technicalDetailsForm_redirect_uri',
            }),
          }
        ),
      },
      {
        field: TFieldNames.ProductionSmartLaunchUrl,
        conditions: [apiResponseErrors?.smart_launch_url_production || errorList['production/smart_launch_url_production']],
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'technicalDetailsForm_smart_launch_url',
            }),
          }
        ),
      },
    ]
    validationRules.forEach(({ field, conditions, message }) => {
      if (conditions.some(Boolean)) {
        setError(field, { message });
      }
    });
    return () => {
      clearErrors();
    };
  }, [apiResponseErrors, submitToPublishResponse, errorList]);


  const convertTechDetailData = (response?: TTechDetails): TTechDetails | undefined => {
    if (!response) {
      return
    }

    handleOAuthClientMechanism(response);
    handleOAuthClientType(response)

    if (response?.client_secret) {
      setSecret(response?.client_secret)
    }
    if (response?.production.client_id) {
      setClientid(response?.production.client_id)
    }
    handleScope(response)
    handleLaunchTypeStatus(response);

    return {
      ...response,
      
      production: {
        client_id: getProductionField(response, 'client_id'),
        smart_launch_url_production: getProductionField(response, 'smart_launch_url_production'),
        redirect_uri_production: getProductionField(response, 'redirect_uri_production'),
      },
    };
  };
  const handleOAuthClientMechanism = (response: TTechDetails) => {
    if (response.oauthclientmechanism === ToAuthClientMechanism.JWKS) {
      setisJwks(true);
    } else if (response.oauthclientmechanism === ToAuthClientMechanism.CLIENTSECRET) {
      setisSecret(true);
      handleClientSecretExpiry(response);
    }
  };
  const handleClientSecretExpiry = (response: TTechDetails) => {
    if (response.secret_expiry_date) {
      setisResetClientSecret(true);
    }
  };
  const handleOAuthClientType = (response: TTechDetails) => {
    if (response.oauthclienttype === TAuthTypes.CONFIDENTIAL) {
      setisConfidential(true);
    } else if (response.oauthclienttype === TAuthTypes.PUBLIC) {
      setisConfidential(false);
    }
  };

  const handleScope = (response: TTechDetails) => {
    const isScopeReadAll = response.scope_read_all || response.application_launch_type?.name === TLaunchTypeNames['BACKEND_SERVICE'];
    setallscope(isScopeReadAll);
    setValue("scope_read_all", isScopeReadAll, { shouldDirty: false });
  };
  const handleLaunchTypeStatus = (response: TTechDetails) => {
    const isCreated = response.status === TAppStatus.CREATED;
    setIsLauchTypeLocked(!isCreated);
  };
  const getProductionField = (response: TTechDetails, field: keyof TProduction, fallback = '') => {
    return response.production?.[field] ?? fallback;
  };

  useEffect(() => {
    (async () => {
      await techDetailsContext.fetchTechDetailsContext();
      const response = await techDetails.fetchTechDetails(appId);

      setSmartVersion(response?.smart_version);

      reset(convertTechDetailData(response));
      setInitialData(convertTechDetailData(response));
    })();
  }, []);

  const handleCancelEdit = () => {
    reset(convertTechDetailData(initialData));
    setIsProductionDisabled(false);
    isEditOff();
  };

  const handleClientSecretReset = async () => {
    const response = await resetSecret(appId);

    if (response.errors) {
      setResponseErrors(response);
      setInitialData(convertTechDetailData(response.data));
    } else {
      reset(convertTechDetailData(response.data));
      setInitialData(convertTechDetailData(response.data));
      setValue('client_secret', response.client_secret);
      setSecret(response?.client_secret);
      setisResetClientSecret(false);
      isEditOff();
    }
  };

  const smartUrl = useWatch({
    control,
    name: 'production.smart_launch_url_production',
  });

  const redirectUrl = useWatch({
    control,
    name: 'production.redirect_uri_production',
  });

  const checkedLaunchType = useWatch({
    control,
    name: 'application_launch_type',
  });

  const checkedOAuthType = useWatch({
    control,
    name: 'oauthclienttype',
  });

  const checkedOAuthMechanism = useWatch({
    control,
    name: 'oauthclientmechanism',
  });

  const checkedTokenIntrospection = useWatch({
    control,
    name: 'token_introspection',
  });

  const [isConfidential, setisConfidential] = useState(false);
  const [isJwks, setisJwks] = useState(false);
  const [isSecret, setisSecret] = useState(false);
  const [secret, setSecret] = useState('');
  const [clientid, setClientid] = useState('');
  const [isResetClientSecret, setisResetClientSecret] = useState(false);
  const [smart_version, setSmartVersion] = useState('V1');

  const isLaunchContext = checkedLaunchType?.type === TLaunchType['STANDALONE'];

  const isIndiviualScopesDisplayed =
    checkedLaunchType?.type === TLaunchType['STANDALONE'] ||
    checkedLaunchType?.name === TLaunchTypeNames['RUN_OUTSIDE_OF_PATIENT_CHART'] ||
    checkedLaunchType?.name === TLaunchTypeNames['RUN_INSIDE_PATIENT_CHART'];

  const isPopulationScopesDisplayed = checkedLaunchType?.name !== TLaunchTypeNames['BACKEND_SERVICE'];

  const isSystemScopesDisplayed = checkedLaunchType?.name === TLaunchTypeNames['BACKEND_SERVICE'];

  const isPatientUserType = () => {
    const dev_context = localStorage.getItem(MARKETPLACE_DEV_CONTEXT);
    if (dev_context) {
      const user_type: string = JSON.parse(dev_context)?.type;
      return user_type.toLowerCase() === UserType.PATIENT.toLowerCase();
    }
    return false;
  };

  const handleLaunchType = (type: TApplicationLaunchType) => {
    if (type?.name !== checkedLaunchType?.name) {
      setValue('application_launch_type', type, { shouldDirty: true });
      setValue(`has_patient_context`, false);
      setValue('has_encounter_context', false);

    }
    clearErrors('application_launch_type');
    if (type?.name !== "BACKEND_SERVICE") {
      if (isPopulationScopesDisplayed) {
        setCheckedScope('population');
      }

      if (isIndiviualScopesDisplayed) {
        setCheckedScope('individual');
      }
      setallscope(false)
      setValue('scope_read_all', false, { shouldDirty: true });
      setValue('scopes', []);
    }
  };

  const handleBackendLaunchType = (type: TApplicationLaunchType) => {
    handleLaunchType(type);
    setValue('oauthclienttype', TAuthTypes.CONFIDENTIAL, { shouldDirty: true });
    setisConfidential(true)
    setisJwks(true);
    setisSecret(false);
    setisResetClientSecret(false);
    setValue('jwks', "")
    setValue('client_secret', "")
    setValue('oauthclientmechanism', ToAuthClientMechanism.JWKS, { shouldDirty: true });
    setValue(`has_patient_context`, false)
    setValue('has_encounter_context', false)
    setCheckedScope('system');
    setallscope(true)
    setValue('scope_read_all', true, { shouldDirty: true });
    const scopeList = techDetailsContext.data?.scopes ? techDetailsContext.data?.scopes : [];
    if (scopeList && scopeList.length > 0) {
      scopeList.forEach(item => {
        if (isSystemScopesDisplayed) {
          item.system_r = true;
          item.system_w = false;
          item.individual_patient_r = false;
          item.individual_patient_w = false;
          item.patient_population_r = false;
          item.patient_population_w = false;
        }
      })

      if (techDetailsContext.data?.scopes) {
        setValue('scopes', scopeList);
      }
    }
    else {
      setValue('scopes', []);
    }
  };

  const handleAuthType = (controlname: string) => {
    if (controlname === "Public") {
      setValue('oauthclienttype', TAuthTypes.PUBLIC, { shouldDirty: true });
      setisConfidential(false)
      setisSecret(false);
      setisResetClientSecret(false);
      setisJwks(false)
      setValue('jwks', "")
      setValue('client_secret', "")

    }
    else if (controlname === "Confidential") {
      setValue('oauthclienttype', TAuthTypes.CONFIDENTIAL, { shouldDirty: true });
      setisConfidential(true)
      setisSecret(false);
      setisResetClientSecret(false);
      setisJwks(false)
      setValue('jwks', "")
      setValue('client_secret', "")
    }
  }

  const handleJwks = (controlname: string) => {
    if (controlname === "Jwks") {
      setisJwks(true);
      setisSecret(false);
      setisResetClientSecret(false);
      setValue('oauthclientmechanism', ToAuthClientMechanism.JWKS, { shouldDirty: true });
    }
    else if (controlname === 'Secret') {
      setisSecret(true);
      setisJwks(false)
      setValue('oauthclientmechanism', ToAuthClientMechanism.CLIENTSECRET, { shouldDirty: true });
    }
  }

  const handleTokenIntrospection = (isOnline?: boolean) => {
    setValue('token_introspection', isOnline, { shouldDirty: true });
    trigger();
    clearErrors('token_introspection');
  };

  const handleContext = ({ name, checked }: { name: 'patient' | 'encounter'; checked: boolean }) => {
    setValue(`has_${name}_context`, checked, {
      shouldDirty: true,
      shouldValidate: true,
    });
    clearErrors('has_encounter_context');
    clearErrors('has_patient_context');
  };

  const clearProdValidationErrors = () => {
    clearErrors('production.smart_launch_url_production');
    clearErrors('production.redirect_uri_production');
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (checkedLaunchType?.name === TLaunchTypeNames['BACKEND_SERVICE']) {
      setValue('token_introspection', true, { shouldDirty: true });
    }

    if (
      checkedLaunchType?.name === TLaunchTypeNames['RUN_INSIDE_PATIENT_CHART'] ||
      checkedLaunchType?.name === TLaunchTypeNames['RUN_OUTSIDE_OF_PATIENT_CHART']
    ) {
      setValue('token_introspection', false, { shouldDirty: true });
    }
  }, [checkedLaunchType?.type]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const scopeList = useWatch({
    control,
    name: 'scopes',
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const jwksValue = useWatch({
    control,
    name: 'jwks',
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const newScopes = scopeList?.map((scope) => {
      return {
        ...scope,
      };
    });

    if (newScopes) setValue('scopes', newScopes);

    if (isPopulationScopesDisplayed) {
      setCheckedScope('population');
      return;
    }

    if (isSystemScopesDisplayed) {
      setCheckedScope('system');
      return;
    }

    if (isIndiviualScopesDisplayed) {
      setCheckedScope('individual');
    }
  }, [isIndiviualScopesDisplayed, isSystemScopesDisplayed, isPopulationScopesDisplayed]);

  
  function handleShowHideButtonVisibility(): void {
    setIsisSecretVisible(prevState => !prevState);
  }

  const handleChangeall = (e: { target: { name: string; checked: boolean; }; }) => {
    const isChecked = e.target.checked;
    setValue('scope_read_all', isChecked, { shouldDirty: true });
    setallscope(isChecked);

    if (isPopulationScopesDisplayed) {
      setCheckedScope('population');
    }

    if (isSystemScopesDisplayed) {
      setCheckedScope('system');
    }

    if (isIndiviualScopesDisplayed) {
      setCheckedScope('individual');
    }

    const scopeList = techDetailsContext.data?.scopes ? techDetailsContext.data?.scopes : [];
    if (isChecked && scopeList && scopeList.length > 0) {
      scopeList.forEach(item => {
        if (isSystemScopesDisplayed) {
          item.system_r = isChecked;
          item.system_w = false;
          item.individual_patient_r = false;
          item.individual_patient_w = false;
          item.patient_population_r = false;
          item.patient_population_w = false;
        }
        else if (isPopulationScopesDisplayed) {
          item.system_r = false;
          item.system_w = false;
          item.individual_patient_r = false;
          item.individual_patient_w = false;
          item.patient_population_r = isChecked;
          item.patient_population_w = false;
        }
        else if (isIndiviualScopesDisplayed) {
          item.system_r = false;
          item.system_w = false;
          item.individual_patient_r = isChecked;
          item.individual_patient_w = false;
          item.patient_population_r = false;
          item.patient_population_w = false;
        }
      })

      if (techDetailsContext.data?.scopes) {
        setValue('scopes', scopeList);
      }
    }
    else {
      setValue('scopes', []);
    }
  };

  const handleProductionToggle = () => {
    clearProdValidationErrors();
    setIsProductionDisabled(!isProductionDisabled);
    setValue('production_toggle', isProductionDisabled, { shouldDirty: true })
  };

  return (
    <form onSubmit={handleSubmit(handleSaveDetails)} data-testid="techDetailsForm">
      <AppDetailsWrapper>
        <div className={classes.headerWrapper}>
          
          <Icon
            data-testId="isEditOnIcon"
            size={Icon.sizes.medium}
            iconClassName={isContentEditable ? classes.editIcon_active : classes.editIcon}
            glyph={Icon.glyphs.svg['pencil']}
            onClick={isEditOn}
          />
        </div>
        <div className={classes.applicationLaunchTypeSection}>
          <AppDetailsTitle
            title={formatMessage({
              id: 'technicalDetailsForm_application_type_title',
            })}
            errors={errors?.application_launch_type?.message}
          />
          <div className={classes.applicationLaunchTypeWrapper}>
            <div className="standaloneTypes">
              <h4 className={classes.launch_type_title}>
                {formatMessage({
                  id: 'technicalDetailsForm_application_type_standalone',
                })}
              </h4>
              <div className={classes.applicationSubTypesWrapper}>
                {standaloneFields?.map((launchType) => (
                  <RadioButton
                    key={launchType.id}
                    rootClassName={classes.applicationSubTypesItem}
                    control={control}
                    label={formatMessage({
                      id: `technicalDetailsForm_application_type_${launchType.name.toLowerCase()}`,
                    })}
                    name={'application_launch_type'}
                    value={launchType.name}
                    fieldId={launchType.name}
                    id={launchType.name}

                    onChange={() => handleLaunchType(launchType)}
                    checked={checkedLaunchType?.id === launchType.id}
                    disabled={!isContentEditable || isLauchTypeLocked}
                  />
                ))}
              </div>
            </div>
            <div className="ehrEmbededTypes">
              <h4 className={classes.launch_type_title}>
                {formatMessage({
                  id: 'technicalDetailsForm_application_type_ehr_embeded',
                })}
              </h4>
              <div className={classes.applicationSubTypesWrapper}>
                {ehrFields?.map((launchType) => (
                  <RadioButton
                    key={launchType.id}
                    rootClassName={classes.applicationSubTypesItem}
                    control={control}
                    label={formatMessage({
                      id: `technicalDetailsForm_application_type_${launchType.name.toLowerCase()}`,
                    })}
                    name={'application_launch_type'}
                    value={launchType.name}
                    fieldId={launchType.name}
                    id={launchType.name}
                    onChange={() => handleLaunchType(launchType)}
                    checked={checkedLaunchType?.id === launchType.id}
                    disabled={!isContentEditable || isLauchTypeLocked || isPatientUserType()}
                  />
                ))}
              </div>
            </div>
            <div className="backEndTypes">
              {backEndFields && (
                <RadioButton
                  control={control}
                  label={formatMessage({
                    id: 'technicalDetailsForm_application_type_backend',
                  })}
                  name={'application_launch_type'}
                  value={backEndFields.name}
                  id={backEndFields.name}
                  data-testId="backEndTypes"
                  fieldId={backEndFields.name}
                  onChange={() => handleBackendLaunchType(backEndFields)}
                  checked={checkedLaunchType?.id === backEndFields.id}
                  disabled={!isContentEditable || isLauchTypeLocked || isPatientUserType()}
                />
              )}
            </div>
          </div>
        </div>
        <div className={classes.authTypeWrapper}>
          <AppDetailsTitle
            title={formatMessage({
              id: 'technicalDetailsForm_auth_type_title',
            })}
          />
          <div className={classes.selectElementsWrapper}>
            <RadioButton
              key={TAuthTypes.PUBLIC}
              rootClassName={classes.selectElementsWrapper}
              control={control}
              label={formatMessage({
                id: 'technicalDetailsForm_client_type_public',
              })}
              name="oauthclienttype"
              value={TAuthTypes.PUBLIC}
              fieldId="oauthclienttype"
              id={TAuthTypes.PUBLIC}
              checked={checkedOAuthType == TAuthTypes.PUBLIC && !(checkedLaunchType?.name === TLaunchTypeNames['BACKEND_SERVICE'])}
              disabled={!isContentEditable || checkedLaunchType?.name === TLaunchTypeNames['BACKEND_SERVICE']}
              onChange={() => handleAuthType('Public')}
            />
            <RadioButton
              key={TAuthTypes.CONFIDENTIAL}
              rootClassName={classes.selectElementsWrapper}
              control={control}
              label={formatMessage({
                id: 'technicalDetailsForm_client_type_confidential',
              })}
              name="oauthclienttype"
              fieldId="oauthclienttype"
              id="oauthclienttype"
              value="confidential"
              checked={checkedOAuthType == TAuthTypes.CONFIDENTIAL || checkedLaunchType?.name === TLaunchTypeNames['BACKEND_SERVICE']}
              disabled={!isContentEditable || !checkedLaunchType}
              onChange={() => handleAuthType('Confidential')}
            />
          </div>
          <div>
            <span className={classes.validationContainer}>{errors.oauthclienttype?.message}</span>
          </div>
          <div className={classes.authTypeText}>
            <span>Set this to Confidential </span> <span className={classes.onlyWord}>only </span>
            <span>
              if your application is able to secure secret data. (Eg your app logic runs on a trusted server.) If your
              app is HTML5 or Javascript, or data is otherwise available in user space, or if your app is a native app,
              leave this set to Public.
            </span>
          </div>
        </div>

        {(isConfidential || checkedOAuthType == TAuthTypes.CONFIDENTIAL) && (
          <div className={classes.authTypeWrapper}>
            <AppDetailsTitle
              title={formatMessage({
                id: 'technicalDetailsForm_auth_mechanism',
              })}
            />
            <div className={classes.selectElementsWrapper}>
              <RadioButton
                key="oauthclientmechanism"
                rootClassName={classes.selectElementsWrapper}
                control={control}
                label={formatMessage({
                  id: 'technicalDetailsForm_confidential_secret_Jwkskey',
                })}
                name="oauthclientmechanism"
                fieldId="oauthclientmechanism"
                value="JWKS"
                id="oauthclientmechanism"
                checked={isJwks && checkedOAuthMechanism == ToAuthClientMechanism.JWKS || checkedLaunchType?.name === TLaunchTypeNames['BACKEND_SERVICE']}
                disabled={!isContentEditable || !checkedLaunchType}
                onChange={() => handleJwks('Jwks')}
              />
              <div className={classes.offlineWrapper}>
                <RadioButton
                  key="oauthclientmechanism"
                  rootClassName={classes.selectElementsWrapper}
                  control={control}
                  label={formatMessage({
                    id: 'technicalDetailsForm_confidential_secret_secretkey',
                  })}
                  name="oauthclientmechanism"
                  fieldId="oauthclientmechanism"
                  value="CLIENTSECRET"
                  id="oauthclientmechanism"
                  checked={isSecret && checkedOAuthMechanism == ToAuthClientMechanism.CLIENTSECRET && !(checkedLaunchType?.name === TLaunchTypeNames['BACKEND_SERVICE'])}
                  disabled={!isContentEditable || checkedLaunchType?.name === TLaunchTypeNames['BACKEND_SERVICE']}
                  onChange={() => handleJwks('Secret')}
                />
                <div className={classes.iconWrapper}>
                  <Icon iconClassName="secretHint" size={Icon.sizes.medium} glyph={Icon.glyphs.svg['information']} />
                  <Tooltip target=".secretHint">
                    {formatMessage({
                      id: 'technicalDetailsForm_secret_tooltip',
                    })}
                  </Tooltip>
                </div>
              </div>
            </div>
            <span className={classes.validationContainer}>{errors.oauthclientmechanism?.message}</span>
          </div>
        )}
        {(isConfidential && isJwks && checkedOAuthMechanism == ToAuthClientMechanism.JWKS) && (
          <div className={classes.jwksWrapper}>
            
            <TextField
              label={formatMessage({
                id: 'technicalDetailsForm_jwks_label',
              })}
              // textareaClassName = {classes.jwkTextArea}
              placeholder={formatMessage({
                id: 'technicalDetailsForm_confidential_placeholder',
              })}
              control={control}
              error={errors.jwks?.message}
              fieldId="jwks"
              name="jwks"
              disabled={!isContentEditable}
              isRequired={isConfidential}
            />
            <div className={classes.iconWrapperjwk}>
              <Icon iconClassName="jwksHint" size={Icon.sizes.tiny} glyph={Icon.glyphs.svg['information']} />
              <Tooltip mouseTrack target=".jwksHint">
                {formatMessage({
                  id: 'technicalDetailsForm_jwks_hint',
                })}
              </Tooltip>
            </div>
            
          </div>
        )}
        {(isConfidential && isSecret && checkedOAuthMechanism === ToAuthClientMechanism.CLIENTSECRET) && (
          <div className={classes.jwksWrapper}>
            <div className={classes.img_wrapper}>
              <TextField
                label={formatMessage({
                  id: 'technicalDetailsForm_secret',
                })}
                fieldId="client_secret"
                name="client_secret"
                control={control}
                disabled={true}
                inputClassName={classes.secretInput}
                type={isSecretVisible ? 'text' : 'password'}

              />
              {secret && (
                <button
                  className={classes.toggle_button}
                  onClick={handleShowHideButtonVisibility}
                >
                  {isSecretVisible ? 'Hide' : 'Show'}
                </button>
              )}
              <img
                src={copyIcon}
                className={classes.icon}
                onClick={() => navigator.clipboard.writeText(secret)}
                alt={""}

              />
            </div>
            {(secret) &&
              <div className={classes.resetButton}>
                <Button
                  id="client_secret_reset"
                  type='button'
                  loading={resetClientSeretLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    handleClientSecretReset();
                  }}
                  disabled={!isContentEditable}
                  tooltip={formatMessage({
                    id: 'technicalDetailsForm_secret_reset_tooltip_admin',
                  })}
                  tooltipOptions={{ autoHide: true, position: 'bottom' }}
                >
                  {formatMessage({
                    id: 'technicalDetailsForm_secret_reset',
                  })}
                </Button>
              </div>
            }
            <Tooltip
              target={`.${classes.icon}`}
              content="Copied!"
              position="left"
              showEvent="click"
              hideDelay={2000}
            />
          </div>
        )}

        {checkedLaunchType?.name !== TLaunchTypeNames['BACKEND_SERVICE'] && (
          <div className={classes.tokenIntrospectionSectionWrapper}>
            <AppDetailsTitle
              title={formatMessage({
                id: 'technicalDetailsForm_token_introspection_title',
              })}
            />
            <div className={classes.tokenIntrospectionWrapper}>
              <div className={classes.tokenIntrospectionSelectElementsWrapper}>
                <div className={classes.offlineWrapper}>
                  <RadioButton
                    label={formatMessage({
                      id: 'technicalDetailsForm_token_introspection_online',
                    })}
                    name="token_introspection"
                    id="token_introspection_online"
                    fieldId="online"
                    checked={checkedTokenIntrospection === true}
                    control={control}
                    disabled={
                      checkedLaunchType?.type === TLaunchType['EHR_EMBEDDED'] ||
                      !isContentEditable ||
                      !checkedLaunchType
                    }
                    onChange={() => handleTokenIntrospection(true)}
                  />
                  <div className={classes.iconWrapper}>
                    <Icon iconClassName="onlineHint" size={Icon.sizes.medium} glyph={Icon.glyphs.svg['information']} />
                    <Tooltip target=".onlineHint">
                      {formatMessage({
                        id: 'technicalDetailsForm_token_introspection_online_hint',
                      })}
                    </Tooltip>
                  </div>
                </div>
                <div className={classes.offlineWrapper}>
                  <RadioButton
                    label={formatMessage({
                      id: 'technicalDetailsForm_token_introspection_offline',
                    })}
                    name="token_introspection"
                    control={control}
                    id="token_introspection_offline"
                    fieldId="offline"
                    checked={checkedTokenIntrospection === false}
                    disabled={!isContentEditable || !checkedLaunchType}
                    onChange={() => handleTokenIntrospection(false)}
                  />
                  <div className={classes.iconWrapper}>
                    <Icon iconClassName="offlineHint" size={Icon.sizes.medium} glyph={Icon.glyphs.svg['information']} />
                    <Tooltip target=".offlineHint" rootClassName={classes.tooltip}>
                      {formatMessage({
                        id: 'technicalDetailsForm_token_introspection_offline_hint',
                      })}
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className={classes.customError}>{errors?.token_introspection?.message}</div>
            </div>
          </div>
        )}
        {isLaunchContext && (
          <div className={classes.launchContextSectionWrapper}>
            <div className={classes.launchContextTitleWrapper}>
              <AppDetailsTitle
                title={formatMessage({
                  id: 'technicalDetailsForm_launch_context_title',
                })}
              />
              <div className={classes.iconWrapper}>
                <Icon iconClassName="launchContextHint" size={Icon.sizes.tiny} glyph={Icon.glyphs.svg['information']} />
                <Tooltip mouseTrack target=".launchContextHint">
                  {formatMessage({
                    id: 'technicalDetailsForm_launch_context_title_hint',
                  })}
                </Tooltip>
              </div>
            </div>
            <div className={classes.selectElementsWrapper}>
              <Checkbox
                label={formatMessage({
                  id: 'technicalDetailsForm_launch_context_patient',
                })}
                control={control}
                fieldId="patient"
                id="launch_context_patient"
                name="has_patient_context"
                error={errors.has_patient_context?.message}
                disabled={!isContentEditable || !checkedLaunchType}
                onChange={(e) =>
                  handleContext({
                    name: 'patient',
                    checked: e.checked,
                  })
                }
              />
              <Checkbox
                label={formatMessage({
                  id: 'technicalDetailsForm_launch_context_encounter',
                })}
                control={control}
                fieldId="encounter"
                id="launch_context_encounter"
                name="has_encounter_context"
                error={errors?.has_encounter_context?.message}
                onChange={(e) =>
                  handleContext({
                    name: 'encounter',
                    checked: e.checked,
                  })
                }
                disabled={!isContentEditable || !checkedLaunchType}
              />
            </div>
          </div>
        )}
        <div className={classes.scopesSectionWrapper}>  
          <AppDetailsTitle
            title={formatMessage({
              id: 'technicalDetailsForm_launch_scopes_title',
            })}
            /* @ts-ignore */
            errors={errors?.scopes?.message}
          />
          <div className={classes.scopesButtonsWrapper}>
            <div className={classes.selectElementsWrapper}>
              {isPopulationScopesDisplayed && (
                <Button
                  disabled={isButtonDisabled('population')}
                  id="patient_population"
                  variant={checkedScope === 'population' ? 'secondary' : 'primary'}
                  onClick={(e) => {
                    e.preventDefault();
                    setCheckedScope('population');
                  }}
                >
                  {formatMessage({
                    id: 'technicalDetailsForm_scopes_patient_population',
                  })}
                </Button>
              )}
              {isIndiviualScopesDisplayed && (
                <Button
                  disabled={isButtonDisabled('individual')}
                  variant={checkedScope === 'individual' ? 'secondary' : 'primary'}
                  id="patient_individual"
                  onClick={(e) => {
                    e.preventDefault();
                    setCheckedScope('individual');
                  }}
                  type="button"
                >
                  {formatMessage({
                    id: 'technicalDetailsForm_scopes_patient_individual',
                  })}
                </Button>
              )}
            </div>

            {isSystemScopesDisplayed && (
              <div className={classes.selectAllContainer}>
                <Checkbox
                  rootClassName={classes.readCheckboxCell}
                  fieldId={"scope_read_all"}
                  name="scope_read_all"
                  disabled={!isContentEditable || allscope}
                  control={control}
                  onChange={(e) => handleChangeall(e)}
                  checked={allscope}
                  label={formatMessage({
                    id: smart_version === 'V1' ? 'technicalDetailsForm_select_all_scope_header' : 'technicalDetailsForm_select_all_scope_header_v2',
                  })}
                  labelClassName={classes.selectalllabel}
                />
              </div>
            )}

            <div style={{ marginTop: "17px" }}>
              <AppDetailsTitle
                title={formatMessage({
                  id: 'technicalDetailsForm_scopes_title_verbiage',
                })}
              />
            </div>

            <div className={classes.scopesContainer}>
              <div className={classes.scopesListWrapper}>
                {techDetailsContext.data?.scopes
                  .slice(0, Math.ceil(techDetailsContext.data?.scopes.length / 2))
                  .map((scope) => (
                    <ScopesItem
                      scope={scope}
                      clearErrors={clearErrors}
                      scopeList={scopeList}
                      checkedScope={checkedScope}
                      key={scope.id}
                      isDisabled={!isContentEditable || !checkedLaunchType || allscope}
                      control={control}
                      setValue={setValue}
                      hint={scope?.name === 'Condition' ? formatMessage({ id: 'technicalDetailsForm_scope_condition_hint_description' }) : ''}
                    />
                  ))}
              </div>
              <div className={classes.scopesListWrapper}>
                {techDetailsContext.data?.scopes
                  .slice(Math.ceil(techDetailsContext.data?.scopes.length / 2))
                  .map((scope) => (
                    <ScopesItem
                      scope={scope}
                      scopeList={scopeList}
                      checkedScope={checkedScope}
                      key={scope.id}
                      clearErrors={clearErrors}
                      isDisabled={!isContentEditable || !checkedLaunchType || allscope}
                      control={control}
                      setValue={setValue}
                      hint={scope?.name === 'Observation' ? formatMessage({ id: 'technicalDetailsForm_scope_observation_hint_description' }) : ''}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
        <div className={classes.sandboxProductionSectionWrapper}>
          
          <div className={classes.productionWrapper}>
            <div className={classes.productionFormHeaderWrapper}>
              <AppDetailsTitle
                className={classes.productionTitle}
                title={formatMessage({
                  id: 'technicalDetailsForm_production_title',
                })}
              />
              <Toggle
                onChange={handleProductionToggle}
                fieldId="production_toggle"
                name="production_toggle"
                control={control}
                isDisabled={!isContentEditable || isCreated}
                checked={isProductionDisabled}
              />
            </div>
            {!(checkedLaunchType?.name === TLaunchTypeNames['BACKEND_SERVICE']) && (
              <><TextField
                isRequired={isProductionDisabled && isContentEditable}
                label={formatMessage({
                  id: 'technicalDetailsForm_smart_launch_url',
                })}
                control={control}
                error={errors.production?.smart_launch_url_production?.message}
                fieldId="production.smart_launch_url_production"
                name="production.smart_launch_url_production"
                maxLength={2049}
                disabled={!isContentEditable || !isProductionDisabled}
              ></TextField><TextField
                isRequired={isProductionDisabled && isContentEditable}
                label={formatMessage({
                  id: 'technicalDetailsForm_redirect_uri',
                })}
                control={control}
                error={errors.production?.redirect_uri_production?.message}
                fieldId="production.redirect_uri_production"
                name="production.redirect_uri_production"
                maxLength={2049}
                onBlur={compareSmartAndRedirectUrl}
                disabled={!isContentEditable || !isProductionDisabled}
              ></TextField></>
            )}

            <div className={classes.img_wrapper}>
              <TextField
                label={formatMessage({
                  id: 'technicalDetailsForm_client_id',
                })}
                fieldId="client_id"
                name="production.client_id"
                control={control}
                error={errors.production?.client_id?.message}
                disabled={true}
                onChange={(e) => {
                  if (!new RegExp(`^(?:(?:${'fhir-app-'}))`).test(e.target.value)) {
                    setValue('production.client_id', 'fhir-app-');
                    return;
                  }
                  setValue('production.client_id', e?.target?.value);
                }}
              />
              <img
                src={copyIcon}
                className={classes.icon}
                onClick={() => navigator.clipboard.writeText(clientid)}
                alt={""}
              />
            </div>
          </div>
        </div>
        {isContentEditable && (
          <div className={classes.footerButtons}>
            <Button onClick={handleCancelEdit} variant="secondary">
              {formatMessage({ id: 'developer_app_desc_form_cancel_button' })}
            </Button>
            <Button type="submit" loading={postPutTechDetailsLoading} disabled={(isValid && !isDirty) || !isValid}>
              {formatMessage({ id: 'developer_app_desc_form_save_button' })}
            </Button>
          </div>
        )}
      </AppDetailsWrapper>
    </form>
  );
};
